/**
 * Image Display Optimizer Hook
 * Provides utilities for optimizing image display in property cards
 */

import { useMemo } from 'react';

interface ImageOptimizationOptions {
  mode?: 'cover' | 'contain' | 'fill';
  enableDepth?: boolean;
  enableSharpness?: boolean;
  hoverScale?: number;
}

interface ImageOptimizationResult {
  combineImages: (mainImage?: string | null, galleryImages?: string[] | null) => string[];
  getStyle: (dimensions: { width: number; height: number }) => React.CSSProperties;
}

export const useImageOptimization = (options: ImageOptimizationOptions = {}): ImageOptimizationResult => {
  const {
    mode = 'cover',
    enableDepth = false,
    enableSharpness = false,
    hoverScale = 1.0
  } = options;

  const combineImages = useMemo(() => {
    return (mainImage?: string | null, galleryImages?: string[] | null): string[] => {
      const images: string[] = [];
      
      // Add main image first if exists
      if (mainImage) {
        images.push(mainImage);
      }
      
      // Add gallery images (excluding main image if it's already in gallery)
      if (galleryImages && Array.isArray(galleryImages)) {
        galleryImages.forEach(img => {
          if (img && img !== mainImage && !images.includes(img)) {
            images.push(img);
          }
        });
      }
      
      // If no images, return default placeholder
      if (images.length === 0) {
        return ['/img/Product/Product (1).jpeg'];
      }
      
      return images;
    };
  }, []);

  const getStyle = useMemo(() => {
    return (): React.CSSProperties => {
      const baseStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        objectFit: mode,
        objectPosition: 'center',
        transition: 'transform 0.3s ease, filter 0.3s ease',
      };

      // Add depth effect if enabled
      if (enableDepth) {
        baseStyle.filter = 'brightness(1.05) contrast(1.02)';
      }

      // Add sharpness if enabled
      if (enableSharpness) {
        baseStyle.imageRendering = 'crisp-edges';
      }

      // Add hover scale if specified
      if (hoverScale !== 1.0) {
        baseStyle.transform = `scale(${hoverScale})`;
      }

      return baseStyle;
    };
  }, [mode, enableDepth, enableSharpness, hoverScale]);

  return {
    combineImages,
    getStyle
  };
};

